import React, { useState, useEffect, useCallback } from 'react';
import { PageShell } from '../../components/Ui';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import {
  buildPayrollLine,
  linesFromSavedPayroll,
  toApiPayrollLines,
} from '../../utils/payrollCalc';
import { escapeHtml } from '../../utils/escapeHtml';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function PayrollTab() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editCtc, setEditCtc] = useState('');
  const [editAbsent, setEditAbsent] = useState('');
  const [processed, setProcessed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [loadErr, setLoadErr] = useState('');

  const { data: staffData, error: staffErr, reload: reloadStaff } = useLiveData(() => ipApi.getStaffDirectory(), []);

  const rebuildFromLive = useCallback(async (list) => {
    setLoadErr('');
    let attendance = [];
    try {
      const att = await ipApi.getHrAttendance({ month });
      attendance = att?.records || [];
    } catch (e) {
      setLoadErr(e.message || 'Could not load attendance for this month — absent days default to 0.');
    }
    return (list || []).map((s) => {
      const absent = (() => {
        let days = 0;
        const id = s.staffId || s.uid;
        for (const r of attendance) {
          if (String(r.staffId) !== String(id)) continue;
          const st = String(r.status || '').toLowerCase();
          if (st === 'absent' || st === 'leave' || st === 'on leave' || st === 'unpaid leave') days += 1;
          else if (st.includes('half')) days += 0.5;
        }
        return days;
      })();
      return buildPayrollLine(s, absent);
    });
  }, [month]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = (staffData?.staff || []).filter((s) => s.active !== false);
      try {
        const data = await ipApi.getHrPayroll(month);
        if (data?.payroll?.lines?.length && !cancelled) {
          setStaff(linesFromSavedPayroll(data.payroll.lines));
          setProcessed(true);
          setErr('');
        } else if (!cancelled) {
          setStaff(await rebuildFromLive(list));
          setProcessed(false);
        }
      } catch (e) {
        if (!cancelled) {
          setStaff(await rebuildFromLive(list));
          setProcessed(false);
          setErr(e.message || 'Could not load saved payroll — showing live estimate.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [staffData, month, rebuildFromLive]);

  const openEdit = (emp) => {
    if (selected?.id === emp.id) {
      setSelected(null);
      return;
    }
    setSelected(emp);
    setEditCtc(String(emp.basic || emp.ctc || ''));
    setEditAbsent(String(emp.absent || 0));
  };

  const saveEdit = async () => {
    if (!selected) return;
    setBusy(true);
    setMsg('');
    setErr('');
    try {
      const ctc = Number(editCtc);
      if (!Number.isFinite(ctc) || ctc < 0) throw new Error('Enter a valid CTC / basic salary.');
      await ipApi.patchStaff(selected.staffId, { ctc });
      const absent = Math.max(0, Number(editAbsent) || 0);
      const next = buildPayrollLine({ ...selected, ctc, role: selected.role, name: selected.name, staffId: selected.staffId, designation: selected.designation }, absent);
      setStaff((rows) => rows.map((r) => (r.id === selected.id ? next : r)));
      setSelected(next);
      setMsg(`CTC saved for ${selected.name}. Re-process payroll to lock this month.`);
      setProcessed(false);
      await reloadStaff();
    } catch (e) {
      setErr(e.message || 'Could not save CTC');
    } finally {
      setBusy(false);
    }
  };

  const recalculateLive = async () => {
    setBusy(true);
    setMsg('');
    setErr('');
    try {
      const list = (staffData?.staff || []).filter((s) => s.active !== false);
      setStaff(await rebuildFromLive(list));
      setProcessed(false);
      setMsg('Recalculated from staff CTC + monthly attendance.');
    } catch (e) {
      setErr(e.message || 'Recalculate failed');
    } finally {
      setBusy(false);
    }
  };

  const processPayroll = async () => {
    setBusy(true);
    setMsg('');
    setErr('');
    try {
      const lines = toApiPayrollLines(staff);
      await ipApi.saveHrPayroll({ month, lines });
      setProcessed(true);
      setMsg(`Payroll processed and saved for ${month}.`);
    } catch (e) {
      setErr(e.message || 'Process failed');
    } finally {
      setBusy(false);
    }
  };

  const exportNeftCsv = () => {
    const rows = [['Beneficiary Name', 'Account No', 'IFSC', 'Amount', 'Remarks']];
    for (const emp of staff) {
      rows.push([
        emp.name,
        emp.bankAccount || emp.accountNo || '',
        emp.ifsc || '',
        String(emp.net || 0),
        `Salary ${month}`,
      ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `NEFT_Salary_${month}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  const exportEsiCsv = () => {
    const rows = [['Staff Name', 'StaffId', 'ESI Number', 'Gross', 'ESI EE', 'ESI ER']];
    for (const emp of staff) {
      rows.push([
        emp.name,
        emp.staffId || emp.id || '',
        emp.esiNo || '',
        String(emp.gross || 0),
        String(emp.esi || 0),
        String(emp.esiEmployer || 0),
      ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ESI_${month}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  const printSlip = (emp) => {
    const win = window.open('', '_blank', 'width=700,height=800');
    if (!win) return;
    const e = escapeHtml;
    win.document.write(`<html><head><title>Payslip</title><style>
      body{font-family:Arial,sans-serif;max-width:650px;margin:20px auto;font-size:13px}
      .header{text-align:center;border-bottom:2px solid #0d6e6a;padding-bottom:10px;margin-bottom:15px}
      table{width:100%;border-collapse:collapse;margin:10px 0}
      td,th{padding:7px 10px;border:1px solid #e2e8f0}th{background:#f1f5f9;font-weight:700}
      .total{font-weight:700;background:#f8fafc}.net{font-size:16px;font-weight:900;color:#0d6e6a}
    </style></head><body>
      <div class="header"><h2>PAYSLIP — ${e(month)}</h2><p>Confidential — Not for distribution</p></div>
      <table><tr><td><strong>Name:</strong> ${e(emp.name)}</td><td><strong>Emp ID:</strong> ${e(emp.id)}</td></tr>
      <tr><td><strong>Department:</strong> ${e(emp.dept)}</td><td><strong>Designation:</strong> ${e(emp.designation)}</td></tr>
      <tr><td><strong>Month:</strong> ${e(month)}</td><td><strong>Days Absent:</strong> ${e(emp.absent)}</td></tr>
      <tr><td><strong>CTC source:</strong> ${emp.ctcSource === 'staff' ? 'Staff CTC' : emp.ctcSource === 'saved' ? 'Saved run' : 'Role default'}</td><td></td></tr></table>
      <table><tr><th>Earnings</th><th>Amount (₹)</th><th>Deductions</th><th>Amount (₹)</th></tr>
      <tr><td>Basic Salary</td><td>${e(emp.basic.toLocaleString('en-IN'))}</td><td>Provident Fund (PF)</td><td>${e(emp.pf.toLocaleString('en-IN'))}</td></tr>
      <tr><td>HRA</td><td>${e(emp.hra.toLocaleString('en-IN'))}</td><td>ESI</td><td>${e(emp.esi.toLocaleString('en-IN'))}</td></tr>
      <tr><td>Travel Allowance</td><td>${e(emp.ta.toLocaleString('en-IN'))}</td><td>Professional Tax</td><td>${e(emp.pt.toLocaleString('en-IN'))}</td></tr>
      <tr><td>DA</td><td>${e(emp.da.toLocaleString('en-IN'))}</td><td>TDS / Income Tax</td><td>${e(emp.tds.toLocaleString('en-IN'))}</td></tr>
      <tr><td>Special Allowance</td><td>${e(emp.special.toLocaleString('en-IN'))}</td><td>Loan Deduction</td><td>${e(emp.loans.toLocaleString('en-IN'))}</td></tr>
      <tr><td></td><td></td><td>Absent Deduction</td><td>${e((emp.absentDeduction || 0).toLocaleString('en-IN'))}</td></tr>
      <tr class="total"><td>Gross Salary</td><td>${e((emp.gross || 0).toLocaleString('en-IN'))}</td><td>Total Deductions</td><td>${e((emp.totalDeductions || 0).toLocaleString('en-IN'))}</td></tr>
      </table><div style="text-align:center;padding:15px 0"><span class="net">NET SALARY: ₹${e((emp.net || 0).toLocaleString('en-IN'))}</span></div>
      <p style="text-align:center;font-size:11px;color:#94a3b8">This is a system-generated payslip. No signature required.</p>
    </body></html>`);
    win.document.close(); win.print();
  };

  const totalGross = staff.reduce((s, e) => s + (e.gross || 0), 0);
  const totalNet = staff.reduce((s, e) => s + (e.net || 0), 0);
  const totalPf = staff.reduce((s, e) => s + (e.pf || 0), 0);
  const totalEsi = staff.reduce((s, e) => s + (e.esi || 0), 0);

  return (
    <div>
      <div className="info-banner mb">
        Payroll uses each staff member&apos;s saved <strong>CTC</strong> (Staff &amp; Logins) and <strong>absent / leave days</strong> from HR attendance for the selected month. Role defaults apply only when CTC is not set.
      </div>
      {(staffErr || loadErr) && <div className="error mb">{staffErr || loadErr}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Total Staff', val: staff.length, color: '#6366f1' }, { label: 'Gross Payroll', val: inr(totalGross), color: '#3b82f6' }, { label: 'Net Payable', val: inr(totalNet), color: '#10b981' }, { label: 'PF (EE+ER)', val: inr(staff.reduce((s, e) => s + (e.pf || 0) + (e.pfEmployer || e.pf || 0), 0)), color: '#f59e0b' }, { label: 'ESI (EE+ER)', val: inr(staff.reduce((s, e) => s + (e.esi || 0) + (e.esiEmployer || 0), 0)), color: '#8b5cf6' }].map((s) => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 16, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="field" style={{ margin: 0 }}><label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Month</label><input type="month" className="input" value={month} onChange={(e) => setMonth(e.target.value)} /></div>
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={recalculateLive}>Recalculate from CTC + attendance</button>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={processPayroll}>{busy ? 'Saving…' : processed ? 'Re-process payroll' : 'Process payroll'}</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => staff.forEach(printSlip)}>🖨 Print all payslips</button>
        <button type="button" className="btn btn-secondary btn-sm" disabled={!staff.length} onClick={exportNeftCsv}>📤 NEFT CSV</button>
        <button type="button" className="btn btn-secondary btn-sm" disabled={!staff.length} onClick={exportEsiCsv}>📤 ESI CSV</button>
      </div>
      {msg && <div className="info-banner mb">{msg}</div>}
      {err && <div className="error mb">{err}</div>}
      {selected && (
        <div className="card mb" style={{ borderLeft: '4px solid #0d6e6a' }}>
          <h3 style={{ marginTop: 0 }}>Edit — {selected.name}</h3>
          <div className="form-grid">
            <div className="field"><label>Monthly CTC / Basic (₹)</label><input type="number" className="input" value={editCtc} onChange={(e) => setEditCtc(e.target.value)} /></div>
            <div className="field"><label>Absent days (override)</label><input type="number" step="0.5" min="0" className="input" value={editAbsent} onChange={(e) => setEditAbsent(e.target.value)} /></div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSelected(null)}>Cancel</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={saveEdit}>{busy ? 'Saving…' : 'Save CTC & apply'}</button>
          </div>
        </div>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Salary Register — {month}{processed ? ' (saved)' : ' (draft)'}</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead>
              <tr><th>Emp ID</th><th>Name</th><th>Dept</th><th>CTC</th><th>Absent</th><th>Gross (₹)</th><th>Deductions (₹)</th><th>Net Payable (₹)</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {staff.map((emp) => (
                <tr key={emp.id}>
                  <td style={{ fontFamily: 'monospace', color: '#6366f1', fontWeight: 700 }}>{emp.id}</td>
                  <td style={{ fontWeight: 700 }}>{emp.name}</td>
                  <td>{emp.dept}</td>
                  <td>
                    <div style={{ fontWeight: 700 }}>{(emp.basic || 0).toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: 10, color: emp.ctcSource === 'staff' ? '#10b981' : '#f59e0b' }}>{emp.ctcSource === 'staff' ? 'Staff CTC' : emp.ctcSource === 'saved' ? 'Saved' : 'Role default'}</div>
                  </td>
                  <td style={{ fontWeight: 700, color: emp.absent > 0 ? '#dc2626' : '#64748b' }}>{emp.absent}</td>
                  <td style={{ fontWeight: 700, fontFamily: 'Outfit,sans-serif', color: '#3b82f6' }}>{(emp.gross || 0).toLocaleString('en-IN')}</td>
                  <td style={{ fontWeight: 700, fontFamily: 'Outfit,sans-serif', color: '#f97316' }}>{(emp.totalDeductions || 0).toLocaleString('en-IN')}</td>
                  <td style={{ fontWeight: 900, fontFamily: 'Outfit,sans-serif', color: '#10b981', fontSize: 14 }}>{(emp.net || 0).toLocaleString('en-IN')}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button type="button" className="btn btn-sm btn-primary" style={{ fontSize: 10 }} onClick={() => printSlip(emp)}>🖨 Payslip</button>
                      <button type="button" className="btn btn-sm btn-secondary" style={{ fontSize: 10 }} onClick={() => openEdit(emp)}>Edit CTC</button>
                    </div>
                  </td>
                </tr>
              ))}
              {staff.length === 0 && <tr><td colSpan={9} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No staff — add logins under Staff &amp; Logins</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AccountsTab() {
  const { data: expData, error: expError, reload } = useLiveData(() => ipApi.getExpenses(80), []);
  const { data: stats, loading: statsLoading, error: statsError } = useLiveData(() => ipApi.getAnalytics({ period: 'month' }), []);
  const expenses = (expData?.expenses || []).map((e) => ({
    id: e.expenseId,
    date: e.date,
    category: e.category,
    description: e.description,
    amount: e.amount,
    mode: e.paidBy || e.vendor || 'Cash',
    by: e.createdBy || 'Accounts',
  }));
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: 'Utilities', description: '', amount: '', paidBy: 'Bank Transfer' });
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [formOk, setFormOk] = useState('');

  const addExpense = async () => {
    if (!form.description || !form.amount) {
      setFormErr('Description and amount are required.');
      return;
    }
    setSaving(true);
    setFormErr('');
    setFormOk('');
    try {
      await ipApi.createExpense({
        description: form.description,
        category: form.category,
        amount: Number(form.amount),
        paidBy: form.paidBy,
        date: new Date().toISOString().slice(0, 10),
      });
      setShowForm(false);
      setForm({ category: 'Utilities', description: '', amount: '', paidBy: 'Bank Transfer' });
      setFormOk('Expense saved.');
      reload();
    } catch (e) {
      setFormErr(e.message || 'Could not save expense');
    } finally {
      setSaving(false);
    }
  };

  const REVENUE = [
    { category: 'OP Collections (this month)', amount: Number(stats?.opReceiptsTotal || 0), pct: 0 },
    { category: 'All receipts (this month)', amount: Number(stats?.receiptsTotal || 0), pct: 0 },
    { category: 'IP outstanding (live)', amount: Number(stats?.ipOutstanding || 0), pct: 0 },
  ];
  const totalRevenue = Number(stats?.receiptsTotal || 0);
  const totalExpense = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const profit = totalRevenue - totalExpense;
  const revenueWithPct = REVENUE.map((r) => {
    const pct = totalRevenue > 0 ? Math.round((r.amount / totalRevenue) * 100) : 0;
    return { ...r, pct };
  });

  return (
    <div>
      <div className="info-banner mb">
        P&amp;L uses live receipts and expenses logged in HCEN. Add expenses below; revenue comes from billing analytics for the current month.
      </div>
      {(expError || statsError || formErr) && <div className="error mb">{formErr || expError || statsError}</div>}
      {formOk && <div className="info-banner mb">{formOk}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Revenue (month)', val: statsLoading ? '…' : inr(totalRevenue), color: '#10b981' }, { label: 'Expenses logged', val: inr(totalExpense), color: '#f97316' }, { label: 'Net Profit/Loss', val: statsLoading ? '…' : inr(profit), color: profit > 0 ? '#10b981' : '#dc2626' }, { label: 'Profit Margin', val: totalRevenue > 0 ? `${Math.round((profit / totalRevenue) * 100)}%` : '—', color: profit > 0 ? '#10b981' : '#dc2626' }].map((s) => (
          <div key={s.label} className="card" style={{ padding: 14, textAlign: 'center', borderTop: `4px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="resp-grid-2col-equal">
        <div className="card">
          <h3 style={{ marginTop: 0, color: '#10b981' }}>📈 Revenue (live)</h3>
          {revenueWithPct.map((r) => (
            <div key={r.category} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{r.category}</span>
                <span style={{ fontWeight: 800, fontFamily: 'Outfit,sans-serif', color: '#10b981' }}>{inr(r.amount)}</span>
              </div>
              <div style={{ background: '#f1f5f9', borderRadius: 4, height: 6 }}>
                <div style={{ width: `${r.pct}%`, background: '#10b981', height: 6, borderRadius: 4 }} />
              </div>
            </div>
          ))}
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>💸 Expense Ledger</h3>
          <div className="table-wrap">
            <table className="data-table" style={{ fontSize: 12 }}>
              <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Amount</th><th>Mode</th></tr></thead>
              <tbody>
                {expenses.map((e) => (
                  <tr key={e.id}>
                    <td>{e.date}</td>
                    <td style={{ fontWeight: 600, fontSize: 11 }}>{e.category}</td>
                    <td style={{ fontSize: 11, color: '#64748b' }}>{e.description}</td>
                    <td style={{ fontWeight: 700, color: '#f97316', fontFamily: 'Outfit,sans-serif' }}>{inr(e.amount)}</td>
                    <td style={{ fontSize: 11 }}>{e.mode}</td>
                  </tr>
                ))}
                {expenses.length === 0 && <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>No expenses logged yet</td></tr>}
              </tbody>
            </table>
          </div>
          {showForm && (
            <div className="form-grid mb" style={{ marginTop: 10 }}>
              <div className="field"><label>Category</label><input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div className="field"><label>Description</label><input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="field"><label>Amount</label><input type="number" className="input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div className="field"><label>Payment mode</label><input className="input" value={form.paidBy} onChange={(e) => setForm({ ...form, paidBy: e.target.value })} /></div>
              <div className="form-actions full">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={addExpense}>{saving ? 'Saving…' : 'Save expense'}</button>
              </div>
            </div>
          )}
          <button type="button" className="btn btn-secondary btn-sm mt" style={{ marginTop: 10 }} onClick={() => setShowForm((s) => !s)}>+ Add Expense</button>
        </div>
      </div>
    </div>
  );
}

export default function PayrollPage() {
  const [tab, setTab] = useState('payroll');
  return (
    <PageShell title="Payroll & Accounts" subtitle="CTC-based salary · Attendance deductions · PF/ESI/TDS · Payslips · Revenue vs Expense · P&L"
      actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>📊 Monthly Report</button>}>
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'payroll', label: '💰 Payroll Processing' }, { id: 'accounts', label: '📊 Accounts & P&L' }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'payroll' && <PayrollTab />}
      {tab === 'accounts' && <AccountsTab />}
    </PageShell>
  );
}
